import { Camera, Images, VideoOff } from 'lucide-react'
import { useEffect, useId, useRef, type ChangeEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { ActionInfo } from './ActionInfo'
import { Button } from './Button'
import { useHasCamera } from './useHasCamera'
import { useSwipeDismiss } from './useSwipeDismiss'
import './photoSource.css'

/** Почему «Сфотографировать» выключен — словами экрана, видно рядом с пунктом. */
export const NO_CAMERA_NOTE = 'На этом устройстве нет камеры — выберите снимок из галереи или файлов.'

export interface PhotoSourceSheetProps {
  /** Лист открыт. Скрытые поля выбора файла живут всегда: системный диалог открывается по нажатию на пункт. */
  open: boolean
  onClose: () => void
  /** Человек выбрал или снял кадр. */
  onFile: (file: File) => void
  /** Записи реестра действий (UI-ACTIONS) для пунктов; нет — пункт без ⓘ. */
  cameraActionId?: string
  galleryActionId?: string
  cancelActionId?: string
}

function Option({
  icon,
  title,
  note,
  noteId,
  disabled,
  onClick,
  actionId,
  disabledReason,
}: {
  icon: ReactNode
  title: string
  note: string
  noteId: string
  disabled: boolean
  onClick: () => void
  actionId: string | undefined
  disabledReason: string | null
}) {
  const button = (
    <button
      type="button"
      className="krp-sheet__option"
      disabled={disabled}
      aria-describedby={noteId}
      data-unavailable={disabled ? 'true' : undefined}
      onClick={onClick}
    >
      <span className="krp-sheet__icon">{icon}</span>
      <span className="krp-sheet__text">
        <span className="krp-sheet__title">{title}</span>
        <span id={noteId} className="krp-sheet__note">
          {note}
        </span>
      </span>
    </button>
  )
  if (actionId === undefined) return button
  return (
    <ActionInfo id={actionId} label={title} disabled={disabled} disabledReason={disabledReason} layout="block">
      {button}
    </ActionInfo>
  )
}

/**
 * Откуда взять снимок (RP-FB57): нижний лист с двумя крупными пунктами — «Сфотографировать» и «Выбрать из галереи или файлов» —
 * и «Отмена». Общий для всех мест, где человек добавляет фото товара (карточка 1С, общий товар).
 *
 * Пункты открывают СИСТЕМНЫЙ выбор: камеру телефона (`capture`) и галерею/файлы/диск (без `capture`). Оба поля принимают `image/*`:
 * на Android Chrome список конкретных типов (jpeg, png, …) убирает из системного окна камеру и галерею и оставляет один выбор
 * документов, поэтому общий тип нужен именно для телефона. Нажатие на скрытое поле — прямо в обработчике нажатия на пункт: браузер
 * открывает системный диалог только по жесту человека, откладывать нельзя.
 *
 * Закрывают лист «Отмена», Esc, тап мимо листа и смахивание вниз (жест не единственный способ). Без камеры (компьютер)
 * пункт «Сфотографировать» выключен значком и словами, а не только цветом.
 */
export function PhotoSourceSheet({ open, onClose, onFile, cameraActionId, galleryActionId, cancelActionId }: PhotoSourceSheetProps) {
  const hasCamera = useHasCamera()
  const cameraInput = useRef<HTMLInputElement>(null)
  const galleryInput = useRef<HTMLInputElement>(null)
  const sheet = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const cameraNoteId = useId()
  const galleryNoteId = useId()
  const swipe = useSwipeDismiss({ axis: 'down', onDismiss: onClose, disabled: !open })

  useEffect(() => {
    if (open) sheet.current?.focus()
  }, [open])

  const picked = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file !== undefined) onFile(file)
    event.target.value = ''
  }
  const choose = (input: HTMLInputElement | null) => {
    onClose()
    input?.click()
  }

  return (
    <>
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        aria-label="Снимок с камеры"
        onChange={picked}
      />
      <input ref={galleryInput} type="file" accept="image/*" hidden aria-label="Фото из галереи или файла" onChange={picked} />
      {open &&
        createPortal(
          <div
            className="krp-sheet-backdrop"
            onClick={(event) => {
              if (event.target === event.currentTarget) onClose()
            }}
          >
            <div
              ref={sheet}
              className="krp-sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              tabIndex={-1}
              style={swipe.style}
              onKeyDown={(event) => {
                if (event.key !== 'Escape') return
                event.preventDefault()
                event.stopPropagation()
                onClose()
              }}
            >
              <div className="krp-sheet__head" {...swipe.bind()}>
                <span className="krp-sheet__grab" aria-hidden="true" />
                <h2 className="krp-sheet__heading" id={titleId}>
                  Добавить фото
                </h2>
              </div>
              <Option
                icon={hasCamera ? <Camera size={26} aria-hidden="true" /> : <VideoOff size={26} aria-hidden="true" />}
                title="Сфотографировать"
                note={hasCamera ? 'Откроется камера телефона.' : NO_CAMERA_NOTE}
                noteId={cameraNoteId}
                disabled={!hasCamera}
                onClick={() => choose(cameraInput.current)}
                actionId={cameraActionId}
                disabledReason={hasCamera ? null : NO_CAMERA_NOTE}
              />
              <Option
                icon={<Images size={26} aria-hidden="true" />}
                title="Выбрать из галереи или файлов"
                note="Снимок, который уже есть на устройстве."
                noteId={galleryNoteId}
                disabled={false}
                onClick={() => choose(galleryInput.current)}
                actionId={galleryActionId}
                disabledReason={null}
              />
              <div className="krp-sheet__cancel">
                <Button onClick={onClose} actionId={cancelActionId}>
                  Отмена
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
